// src/pages/Orders/AddAdvancePayment.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dealerService from '../../services/dealer.service';
import creditService from '../../services/credit.service';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import './PaymentsPage.css';
import { useAuth } from '../../context/AuthContext'; 

const PAYMENT_MODES = [
  { value: 'CASH', label: '💵 Cash' }, { value: 'CHEQUE', label: '📄 Cheque' },
  { value: 'UPI', label: '📱 UPI' }, { value: 'NEFT', label: '🏦 NEFT' },
  { value: 'RTGS', label: '🏦 RTGS' }, { value: 'IMPS', label: '🏦 IMPS' },
];

const AddAdvancePayment = () => {
  const navigate = useNavigate();
   const { isSuperAdmin } = useAuth(); 
  const [ownerType, setOwnerType] = useState('DEALER');
  const [dealerSearch, setDealerSearch] = useState('');
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState('');
  const [subDealers, setSubDealers] = useState([]);
  const [selectedSubDealerId, setSelectedSubDealerId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [remark, setRemark] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const resolveCompanyId = () => {
    if (isSuperAdmin) {
      const cid = localStorage.getItem('selectedCompanyId');
      return cid ? parseInt(cid) : null;
    }
    return null; // non-global roles: backend resolves from JWT/scope, no need to pass one
  };

useEffect(() => {
    const cid = resolveCompanyId();
    if (isSuperAdmin && !cid) { setDealers([]); return; }
    dealerService.getAllDealers(cid, 1, 20, dealerSearch)
      .then(res => setDealers(res?.data?.dealers || []))
      .catch(() => {});
  }, [dealerSearch, isSuperAdmin]);

  useEffect(() => {
    setSubDealers([]); setSelectedSubDealerId('');
    if (ownerType !== 'SUB_DEALER' || !selectedDealerId) return;
    api.get('/dealer-visits/sub-dealers')
      .then(res => setSubDealers((res.data?.data || []).filter(sd => sd.dealerId === parseInt(selectedDealerId))))
      .catch(() => {});
  }, [ownerType, selectedDealerId]);

  const validate = () => {
    const e = {};
    if (!selectedDealerId) e.dealer = 'Select a dealer';
    if (ownerType === 'SUB_DEALER' && !selectedSubDealerId) e.subDealer = 'Select a sub-dealer';
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) e.amount = 'Enter a valid amount';
    if (!paymentMode) e.paymentMode = 'Payment mode is required';
    if (!paymentDate) e.paymentDate = 'Payment date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ownerType,
        ownerId: ownerType === 'SUB_DEALER' ? parseInt(selectedSubDealerId) : parseInt(selectedDealerId),
        amount: parseFloat(amount), paymentMode, paymentDate,
        referenceNumber: referenceNumber || undefined, remark: remark || undefined,
      };
      const res = await creditService.createAdvancePayment(payload);
      if (res.success) {
  setToast({ message: 'Advance payment submitted for review!', type: 'success' });   // was: '...recorded and credited!'
  setTimeout(() => navigate('/orders/payments'), 1200);
}
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to record advance payment', type: 'error' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="pay-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="pay-header">
        <h4 className="pay-title">Record Advance Payment</h4>
        <p className="pay-subtitle">Credit a dealer or sub-dealer's account ahead of any order</p>
      </div>
      {isSuperAdmin && !localStorage.getItem('selectedCompanyId') ? (
  <div className="pay-empty" style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>
    Select a company from the top bar to add an advance payment.
  </div>
) : (
      <div className="pay-table-container" style={{ padding: 20, maxWidth: 600 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['DEALER', 'SUB_DEALER'].map(t => (
            <button key={t} type="button" onClick={() => setOwnerType(t)}
              style={{ flex: 1, padding: '10px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
                border: ownerType === t ? '2px solid #4caf50' : '1px solid #dee2e6',
                background: ownerType === t ? '#e8f5e9' : '#fff', color: ownerType === t ? '#2e7d32' : '#495057' }}>
              {t === 'DEALER' ? '🏪 Dealer' : '🏬 Sub-Dealer'}
            </button>
          ))}
        </div>

        <div className="pay-field" style={{ marginBottom: 12 }}>
          <label className="pay-label">Search Dealer</label>
          <input className="pay-input" value={dealerSearch} onChange={e => setDealerSearch(e.target.value)} placeholder="Type dealer name..." />
        </div>
        <div className="pay-field" style={{ marginBottom: 12 }}>
          <label className="pay-label">Dealer <span className="required">*</span></label>
          <select className={`pay-input ${errors.dealer ? 'error' : ''}`} value={selectedDealerId} onChange={e => setSelectedDealerId(e.target.value)}>
            <option value="">-- Select Dealer --</option>
            {dealers.map(d => <option key={d.id} value={d.id}>{d.dealerShopName} — {d.ownerName}</option>)}
          </select>
          {errors.dealer && <span className="pay-error">{errors.dealer}</span>}
        </div>

        {ownerType === 'SUB_DEALER' && (
          <div className="pay-field" style={{ marginBottom: 12 }}>
            <label className="pay-label">Sub-Dealer <span className="required">*</span></label>
            <select className={`pay-input ${errors.subDealer ? 'error' : ''}`} value={selectedSubDealerId}
              disabled={!selectedDealerId} onChange={e => setSelectedSubDealerId(e.target.value)}>
              <option value="">{!selectedDealerId ? '-- Select Dealer first --' : '-- Select Sub-Dealer --'}</option>
              {subDealers.map(sd => <option key={sd.id} value={sd.id}>{sd.subDealerShopName} — {sd.ownerName}</option>)}
            </select>
            {errors.subDealer && <span className="pay-error">{errors.subDealer}</span>}
          </div>
        )}

        <div className="pay-row">
          <div className="pay-field">
            <label className="pay-label">Amount (₹) <span className="required">*</span></label>
            <input type="number" className={`pay-input ${errors.amount ? 'error' : ''}`} value={amount} onChange={e => setAmount(e.target.value)} min="0.01" step="0.01" />
            {errors.amount && <span className="pay-error">{errors.amount}</span>}
          </div>
          <div className="pay-field">
            <label className="pay-label">Payment Mode <span className="required">*</span></label>
            <select className={`pay-input ${errors.paymentMode ? 'error' : ''}`} value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
              <option value="">Select Mode</option>
              {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {errors.paymentMode && <span className="pay-error">{errors.paymentMode}</span>}
          </div>
        </div>

        <div className="pay-row">
          <div className="pay-field">
            <label className="pay-label">Payment Date <span className="required">*</span></label>
            <input type="date" className={`pay-input ${errors.paymentDate ? 'error' : ''}`} value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
            {errors.paymentDate && <span className="pay-error">{errors.paymentDate}</span>}
          </div>
          <div className="pay-field">
            <label className="pay-label">Reference No.</label>
            <input className="pay-input" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} placeholder="UTR / Cheque / UPI Ref" />
          </div>
        </div>

        <div className="pay-field" style={{ marginBottom: 16 }}>
          <label className="pay-label">Remark</label>
          <textarea className="pay-textarea" rows={2} value={remark} onChange={e => setRemark(e.target.value)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="pay-btn-secondary" onClick={() => navigate('/orders/payments')} disabled={isSubmitting}>Cancel</button>
          <button className="pay-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : '✓ Record & Credit'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default AddAdvancePayment;